import React, { useState, useEffect } from 'react';
import { WorkspaceProfile, ApplicabilityDecision } from '../types/decision';
import { SyncAdapterConfig, SyncResult } from '../types/sync';
import { ControlObligation, StatutoryProvision } from '../types/obligation';
import { createSyncEnvelope, importSyncEnvelope, syncWithByodEndpoint } from '../utils/syncEngine';
import { exportControlsToCsv, exportProvisionsToCsv } from '../utils/csvExporter';
import { getSyncConfig, saveSyncConfig } from '../utils/indexedDbStorage';
import {
  X,
  Cloud,
  Download,
  Upload,
  Database,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  FileCode,
  RefreshCw,
  HardDrive,
  ShieldCheck,
  Zap,
  Trash2
} from 'lucide-react';

interface ByodSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: WorkspaceProfile;
  controls: ControlObligation[];
  provisions: StatutoryProvision[];
  decisions: Record<string, ApplicabilityDecision>;
  onRefreshDecisions: () => void;
}

export default function ByodSyncModal({
  isOpen,
  onClose,
  workspace,
  controls,
  provisions,
  decisions,
  onRefreshDecisions
}: ByodSyncModalProps) {
  if (!isOpen) return null;

  const [activeTab, setActiveTab] = useState<'export' | 'byod' | 'storage'>('export');
  const [syncConfig, setSyncConfig] = useState<SyncAdapterConfig>({
    adapter_type: 'local_file',
    endpoint_url: '',
    api_key: ''
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [importNotice, setImportNotice] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    getSyncConfig().then(setSyncConfig);
  }, [isOpen]);

  const handleExportJson = async () => {
    const envelope = await createSyncEnvelope(workspace);
    const jsonStr = JSON.stringify(envelope, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `DPDP_Decisions_Snapshot_${workspace.entity_name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCsvControls = () => {
    exportControlsToCsv(controls, decisions, workspace);
  };

  const handleExportCsvProvisions = () => {
    exportProvisionsToCsv(provisions, decisions, workspace);
  };

  const handleImportJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const envelope = JSON.parse(text);
        const res = await importSyncEnvelope(envelope);
        setImportNotice(res);
        if (res.success) {
          onRefreshDecisions();
        }
      } catch (err: any) {
        setImportNotice({
          success: false,
          message: `Import failed: ${err?.message || 'Invalid JSON file'}`
        });
      }
    };
    reader.readAsText(file);
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveSyncConfig(syncConfig);
    setSyncResult({
      success: true,
      message: 'BYOD configuration saved successfully',
      timestamp: new Date().toISOString()
    });
  };

  const handleTestSync = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    try {
      const res = await syncWithByodEndpoint(syncConfig, workspace);
      setSyncResult(res);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs" onClick={onClose} />

      {/* Modal Box */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col z-10">
        
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-900/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-accent/15 text-accent flex items-center justify-center">
              <Cloud className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight">
                Portability, Backup & BYOD Sync Bridge
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                100% Client-side snapshot portability & Bring Your Own Database connector
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 px-6 bg-slate-50/50 dark:bg-slate-900/50">
          <button
            onClick={() => setActiveTab('export')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'export'
                ? 'border-accent text-accent dark:text-accent-light'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export & Import Snapshots</span>
          </button>
          
          <button
            onClick={() => setActiveTab('byod')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'byod'
                ? 'border-accent text-accent dark:text-accent-light'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Database className="w-3.5 h-3.5" />
            <span>BYOD Database Sync</span>
          </button>

          <button
            onClick={() => setActiveTab('storage')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'storage'
                ? 'border-accent text-accent dark:text-accent-light'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <HardDrive className="w-3.5 h-3.5" />
            <span>Offline Diagnostics</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          
          {/* TAB 1: EXPORT & IMPORT */}
          {activeTab === 'export' && (
            <div className="space-y-6">
              
              {/* Notice Message */}
              {importNotice && (
                <div className={`p-4 rounded-2xl text-xs flex items-start gap-2.5 border ${
                  importNotice.success
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800'
                    : 'bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border-rose-300 dark:border-rose-800'
                }`}>
                  {importNotice.success ? <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" /> : <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />}
                  <span>{importNotice.message}</span>
                </div>
              )}

              {/* Export Section */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider">
                  Export Assessment Packages
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  
                  {/* JSON Snapshot */}
                  <button
                    onClick={handleExportJson}
                    className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 hover:border-accent dark:hover:border-accent text-left transition-all group"
                  >
                    <div className="w-8 h-8 rounded-xl bg-accent/15 text-accent flex items-center justify-center mb-2 group-hover:scale-105 transition-transform">
                      <FileCode className="w-4 h-4" />
                    </div>
                    <div className="text-xs font-extrabold text-slate-900 dark:text-slate-100">
                      JSON Snapshot
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      SHA-256 digest bound sync pack
                    </div>
                  </button>

                  {/* CSV Controls Matrix */}
                  <button
                    onClick={handleExportCsvControls}
                    className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 hover:border-emerald-500 text-left transition-all group"
                  >
                    <div className="w-8 h-8 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-2 group-hover:scale-105 transition-transform">
                      <FileSpreadsheet className="w-4 h-4" />
                    </div>
                    <div className="text-xs font-extrabold text-slate-900 dark:text-slate-100">
                      Controls CSV
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      54 operational controls matrix
                    </div>
                  </button>

                  {/* CSV Provisions Matrix */}
                  <button
                    onClick={handleExportCsvProvisions}
                    className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 hover:border-sky-500 text-left transition-all group"
                  >
                    <div className="w-8 h-8 rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-400 flex items-center justify-center mb-2 group-hover:scale-105 transition-transform">
                      <FileSpreadsheet className="w-4 h-4" />
                    </div>
                    <div className="text-xs font-extrabold text-slate-900 dark:text-slate-100">
                      Statutory CSV
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      102 statutory provisions & rules
                    </div>
                  </button>

                </div>
              </div>

              {/* Import Snapshot Section */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-3">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-slate-100">
                  <Upload className="w-4 h-4 text-accent" />
                  <span>Restore / Import JSON Snapshot</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Upload a previously exported `.json` decision envelope to restore decisions, entity facts, and audit logs.
                </p>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportJson}
                  className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-accent file:text-white hover:file:bg-accent-dark cursor-pointer"
                />
              </div>

            </div>
          )}

          {/* TAB 2: BYOD CONFIGURATION */}
          {activeTab === 'byod' && (
            <div className="space-y-5">
              
              <div className="p-4 rounded-2xl bg-accent/5 dark:bg-accent/10 border border-accent/20 space-y-2">
                <div className="flex items-center gap-2 text-xs font-bold text-accent dark:text-accent-light">
                  <ShieldCheck className="w-4 h-4" />
                  <span>Bring Your Own Database (BYOD) Protocol</span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  Connect your offline browser register to your internal self-hosted database (PostgreSQL, SQLite, CouchDB, Supabase, or custom REST webhook). The client dispatches a tamper-evident `SyncEnvelope` with SHA-256 digest validation.
                </p>
              </div>

              {syncResult && (
                <div className={`p-4 rounded-2xl text-xs flex items-start gap-2.5 border ${
                  syncResult.success
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800'
                    : 'bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border-rose-300 dark:border-rose-800'
                }`}>
                  {syncResult.success ? <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" /> : <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />}
                  <div>
                    <div className="font-bold">{syncResult.message}</div>
                    <div className="text-[11px] opacity-80 mt-0.5">{syncResult.timestamp}</div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSaveConfig} className="space-y-4">
                
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    BYOD Sync Endpoint URL
                  </label>
                  <input
                    type="url"
                    value={syncConfig.endpoint_url || ''}
                    onChange={(e) => setSyncConfig({ ...syncConfig, endpoint_url: e.target.value })}
                    placeholder="https://api.yourcompany.internal/dpdp/sync"
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    API Bearer Token / Secret Key (Optional)
                  </label>
                  <input
                    type="password"
                    value={syncConfig.api_key || ''}
                    onChange={(e) => setSyncConfig({ ...syncConfig, api_key: e.target.value })}
                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6Ik..."
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent font-mono"
                  />
                </div>

                <div className="pt-2 flex items-center justify-between gap-3">
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    Save Endpoint Settings
                  </button>

                  <button
                    type="button"
                    onClick={handleTestSync}
                    disabled={isSyncing || !syncConfig.endpoint_url}
                    className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark disabled:opacity-50 transition-all shadow-md shadow-accent/20"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                    <span>{isSyncing ? 'Synchronizing...' : 'Sync & Push Snapshot'}</span>
                  </button>
                </div>

              </form>

            </div>
          )}

          {/* TAB 3: OFFLINE STORAGE DIAGNOSTICS */}
          {activeTab === 'storage' && (
            <div className="space-y-4 text-xs text-slate-600 dark:text-slate-400">
              
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-2">
                <div className="font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-accent" />
                  <span>Browser Storage Telemetry</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 pt-1 font-mono text-[11px]">
                  <div>Storage Engine: <strong className="text-slate-800 dark:text-slate-200">IndexedDB v1</strong></div>
                  <div>Active Workspace: <strong className="text-slate-800 dark:text-slate-200">{workspace.name}</strong></div>
                  <div>Evaluated Decisions: <strong className="text-slate-800 dark:text-slate-200">{Object.keys(decisions).length}</strong></div>
                  <div>Offline Cache: <strong className="text-emerald-600 dark:text-emerald-400">PWA Ready (sw.js)</strong></div>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-rose-50/50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 space-y-2">
                <div className="font-bold text-rose-700 dark:text-rose-400 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  <span>Reset Local Workspace Data</span>
                </div>
                <p className="text-[11px] leading-relaxed">
                  Clearing local storage will permanently wipe local decisions in this browser. Make sure you have exported a JSON snapshot before proceeding.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to clear all local decisions in this browser?')) {
                      localStorage.clear();
                      indexedDB.deleteDatabase('DPDP_Obligations_DB');
                      window.location.reload();
                    }
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-rose-700 dark:text-rose-300 bg-rose-100 dark:bg-rose-900/50 hover:bg-rose-200 transition-colors"
                >
                  Wipe Local Data & Reload
                </button>
              </div>

            </div>
          )}

        </div>

      </div>
    </div>
  );
}
